import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { combineLatest, Observable } from "rxjs";
import { filter, map, withLatestFrom } from "rxjs/operators";

import {
    changeDisplayMode,
    clearFilters,
    clearPriceFilter,
    clearSortBy,
    closeDialog,
    fetchCategoryMetas,
    initGridCategory,
    initGridPage,
    loadMoreProducts,
    openDialog,
    selectGridItem,
    updateGridFilter,
    updateGridFilterPrice,
    updateGridSort,
    updateUrl,
    changePreviousPage,
    reinitializePagesLoaded,
    clearMenuLevel,
    setIsFromPreviousGrid,
    scrollToPreviousProduct,
    updateSeoPaginationHeadLinks,
    updateMissingFacet,
    setBackToGridProperties,
    clearCategories,
} from "../actions/grid.actions";
import { MAX_COMBINED_FACET } from "../constants/grid.constant";
import { getPageViewType } from "../helpers/gtm.helper";
import {
    DisplayMode,
    GridCategory,
    GridFilter,
    GridFilterGroup,
    GridMenuLevel,
    GridProduct,
    GridSortByOption,
    PageType,
    PriceRange,
} from "../model/grid.model";
import { GridState } from "../reducers/grid.reducer";
import {
    selectAlternatives,
    selectBackToGridProperties,
    selectCategory,
    selectCategoryMetas,
    selectCountSelectedFilter,
    selectDisplayMode,
    selectEditos,
    selectFacet,
    selectFilterGroupsWithPrices,
    selectGridHeader,
    selectGridHighlights,
    selectGridHighlightsFilterGroups,
    selectGridType,
    selectHasSelectedFilters,
    selectIsFromPreviousGrid,
    selectIsGridHighlighsEnabled,
    selectIsHeroProduct,
    selectIsPriceSelected,
    selectIsSearchGrid,
    selectLoadMore,
    selectMenuLevel,
    selectMissingFacet,
    selectNbFiltersSelected,
    selectPageNumber,
    selectPendingCategoryMetas,
    selectPendingResultItems,
    selectProducts,
    selectResultItems,
    selectSelectedFilters,
    selectSeoTextOnCategoryMetas,
    selectSortBy,
    selectSwitchFilterActive,
    selectTotal,
    selectCriterias,
} from "../selectors/grid.selectors";

@Injectable()
export class GridFacade {
    /**
     * Selectors
     */

    public category$ = this.store.select(selectCategory);
    public categoryMetas$ = this.store
        .select(selectCategoryMetas)
        .pipe(filter((c) => !!c));
    public seoText$ = this.store.select(selectSeoTextOnCategoryMetas);
    public pendingCategoryMetas$ = this.store.select(
        selectPendingCategoryMetas,
    );
    public menuLevel$ = this.store.select(selectMenuLevel);
    public isSearchGrid$ = this.store.select(selectIsSearchGrid);
    public editos$ = this.store.select(selectEditos);
    public products$ = this.store.select(selectProducts);
    public isFromPreviousGrid$ = this.store.select(selectIsFromPreviousGrid);
    public alternatives$ = this.store.select(selectAlternatives);
    public filterGroups$ = this.store
        .select(selectFilterGroupsWithPrices)
        .pipe(filter((f) => Boolean(f) && f.length > 0));
    public gridHighlights$ = this.store.select(selectGridHighlights);
    public gridHighlightsFilterGroups$ = this.store.select(
        selectGridHighlightsFilterGroups,
    );

    public loadMore$ = this.store.select(selectLoadMore);
    public pageNumber$ = this.store.select(selectPageNumber);

    public total$ = this.store.select(selectTotal);
    public resultItems$ = this.store
        .select(selectResultItems)
        .pipe(filter(Boolean));

    public pendingResultItems$ = this.store.select(selectPendingResultItems);
    public hasSelectedFilters$ = this.store.select(selectHasSelectedFilters);

    public nbFiltersSelected$ = this.store.select(selectNbFiltersSelected);

    public resultGridHeader$ = this.store.select(selectGridHeader);

    public displayMode$ = this.store.select(selectDisplayMode);

    public backToGridProperties$ = this.store.select(
        selectBackToGridProperties,
    );

    public sortBy$ = this.store.select(selectSortBy);

    public isPriceSelected$ = this.store.select(selectIsPriceSelected);

    public selectedFilters$ = this.store.select(selectSelectedFilters);

    public facet$ = this.store.select(selectFacet);

    public criterias$ = this.store.select(selectCriterias);

    public missingFacet$ = this.store.select(selectMissingFacet);

    public gridType$ = this.store.select(selectGridType);

    public pageType$ = this.missingFacet$.pipe(
        withLatestFrom(this.gridType$),
        map(
            ([isMissingFacet, gridType]) =>
                getPageViewType(isMissingFacet)[gridType],
        ),
    );

    public isGridHeroProduct$ = this.store.select(selectIsHeroProduct);

    public isGridHighlighsEnabled$ = this.store.select(
        selectIsGridHighlighsEnabled,
    );

    public pageViewType$ = combineLatest([
        this.store.select(selectFacet),
        this.store.select(selectGridType),
    ]).pipe(
        map(([facet, gridType]) => {
            const combinedFacetsLength = facet?.combinedFacets.length;
            const hasMissingFacet = !!facet?.missingFacet;

            return combinedFacetsLength === MAX_COMBINED_FACET
                ? getPageViewType(hasMissingFacet)["facets"]
                : getPageViewType(hasMissingFacet)[gridType];
        }),
    );

    constructor(private store: Store<GridState>) {}

    public updateSeoPaginationHeadLinks(
        isSeoPaginationEnabled: boolean,
        totalProductsCount: number,
        pageNumber: number,
    ): void {
        this.store.dispatch(
            updateSeoPaginationHeadLinks({
                isSeoPaginationEnabled,
                totalProductsCount,
                pageNumber,
            }),
        );
    }

    public selectCountSelectedFilter$ = (
        filterGroupName: string,
    ): Observable<number> =>
        this.store.select(selectCountSelectedFilter(filterGroupName));

    public selectSwitchFilterActive$ = (
        filterGroupName: string,
    ): Observable<boolean> =>
        this.store.select(selectSwitchFilterActive(filterGroupName));

    public loadMoreProducts(): void {
        this.store.dispatch(loadMoreProducts());
    }

    public updateGridFilter(
        gridFilter: GridFilter,
        filterGroup: GridFilterGroup,
        isHighlightFilter = false,
    ): void {
        this.store.dispatch(
            updateGridFilter({
                filter: gridFilter,
                filterGroup,
                isHighlightFilter,
            }),
        );
    }

    public updateMissingFacet(missingFacet: boolean): void {
        this.store.dispatch(updateMissingFacet({ missingFacet }));
    }

    public updateGridFilterPrice(prices: PriceRange): void {
        this.store.dispatch(updateGridFilterPrice({ prices }));
    }

    public changeDisplayMode(displayMode: DisplayMode): void {
        this.store.dispatch(changeDisplayMode({ displayMode }));
    }

    public initGridCategory(
        category: GridCategory,
        menuLevel: GridMenuLevel,
    ): void {
        this.store.dispatch(initGridCategory({ category, menuLevel }));
    }

    public fetchGridCategoryMetas(categoryPimCode: string): void {
        this.store.dispatch(fetchCategoryMetas({ categoryPimCode }));
    }

    public initGridPage(
        initialValues: Parameters<typeof initGridPage>[0],
    ): void {
        this.store.dispatch(initGridPage(initialValues));
    }

    public scrollToPreviousProduct(): void {
        this.store.dispatch(scrollToPreviousProduct());
    }

    public updateGridSort(sortBy: GridSortByOption): void {
        this.store.dispatch(updateGridSort({ sortBy }));
    }

    public clearFilters(): void {
        this.store.dispatch(clearFilters());
    }

    public clearCategories(): void {
        this.store.dispatch(clearCategories());
    }

    public clearPriceFilter(): void {
        this.store.dispatch(clearPriceFilter());
    }

    public clearSortBy(): void {
        this.store.dispatch(clearSortBy());
    }

    public updateUrl(): void {
        this.store.dispatch(updateUrl());
    }

    public openDialog(): void {
        this.store.dispatch(openDialog());
    }

    public closeDialog(hasChange: boolean): void {
        this.store.dispatch(closeDialog({ gridUpdated: hasChange }));
    }

    public clearMenuLevel(): void {
        this.store.dispatch(clearMenuLevel());
    }

    public selectGridItem(
        product: GridProduct,
        index: number,
        fromQuickbuy: boolean,
    ): void {
        this.store.dispatch(selectGridItem({ product, index, fromQuickbuy }));
    }

    public setBackToGridProperties(position: number, sku?: string): void {
        this.store.dispatch(setBackToGridProperties({ sku, position }));
    }

    public setIsFromPreviousGrid(isFromPreviousGrid: boolean): void {
        this.store.dispatch(setIsFromPreviousGrid({ isFromPreviousGrid }));
    }

    public changePreviousPage(previousPage: PageType): void {
        this.store.dispatch(changePreviousPage({ previousPage }));
    }

    public reinitializePagesLoaded(): void {
        this.store.dispatch(reinitializePagesLoaded());
    }
}
